import React, { useMemo, useState } from "react";
import { ReactComponent as SearchIcon } from "@assets/Icons/IconsSearch.svg";
import { ReactComponent as CloseIcon } from "@assets/Icons/CrossIcon.svg";
import { StatusBadge, StatusBadgeVariant } from "@ui-components/Tag/StatusBadge";
import { SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";
import { ReactComponent as SuccessIcon } from "@assets/Icons/Icon_Success.svg";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { OverviewPanel, PanelLink } from "./OverviewPanel";
import { formatDuration } from "kontrolEdge/helpers/formatDuration";
import { FailoverItem } from "../Types";
import {
  AttentionItem,
  AttentionSeverity,
  StatusFilter,
  countByStatus,
  filterServiceList,
  isOpenIssue,
} from "./overviewModel";

const STATUS_PILLS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "issues", label: "Issues" },
  { value: "up", label: "Up" },
  { value: "nodata", label: "No data" },
];

/** The site picked on the map, which the list narrows to. */
export interface SiteFilter {
  siteIds: string[];
  label: string;
}

const SEVERITY_BADGE: Record<AttentionSeverity, StatusBadgeVariant> = {
  critical: "error",
  warning: "warning",
  resolved: "neutral",
  nodata: "neutral",
  ok: "success",
};

const SEVERITY_EDGE: Record<AttentionSeverity, string> = {
  critical: "bg-semantic-fg-danger",
  warning: "bg-semantic-fg-warning",
  resolved: "bg-semantic-border-secondary",
  nodata: "bg-semantic-border-secondary",
  ok: "bg-semantic-fg-success",
};

/** Rows arrive 25 ms apart; past the tenth they all come with it, so no list takes over ~0.4 s. */
const ROW_STAGGER_MS = 25;
const STAGGERED_ROWS = 10;

/**
 * The customer's tags, whole, on a line of their own under the row: cut down
 * beside the name they could not be read (two truncated chips and a "+1").
 */
function TagList({ tags }: { tags: string[] }) {
  if (tags.length === 0) return null;
  return (
    <span className="mt-1 flex flex-wrap gap-1">
      {tags.map((tag) => (
        <span
          key={tag}
          className="max-w-full truncate rounded bg-semantic-bg-brand-secondary px-1.5 py-px text-[10px] font-semibold text-semantic-text-brand"
          title={tag}
        >
          {tag}
        </span>
      ))}
    </span>
  );
}

function AttentionRow({
  item,
  index,
  onOpen,
}: {
  item: AttentionItem;
  /** Position in the list, for the stagger. */
  index: number;
  onOpen: (item: AttentionItem) => void;
}) {
  const arrive = {
    className: "motion-safe:animate-row-in",
    style: { animationDelay: `${Math.min(index, STAGGERED_ROWS) * ROW_STAGGER_MS}ms` },
  };
  const isActionable = item.path !== null || Boolean(item.outage?.detail);
  const content = (
    <>
      <span className={`w-1 shrink-0 self-stretch rounded-full ${SEVERITY_EDGE[item.severity]}`} aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-semantic-text-primary">{item.title}</span>
        <span className="mt-0.5 block truncate text-xs text-semantic-text-secondary">
          {item.context}
          {item.customer && ` · ${item.customer}`}
        </span>
        <TagList tags={item.tags} />
      </span>
      <StatusBadge variant={SEVERITY_BADGE[item.severity]} label={item.state} withIcon={false} />
    </>
  );

  if (!isActionable) {
    return (
      <li className={`flex items-center gap-3 py-2.5 ${arrive.className}`} style={arrive.style}>
        {content}
      </li>
    );
  }
  return (
    <li className={arrive.className} style={arrive.style}>
      <button
        type="button"
        {...viewHref(item.path)}
        onClick={() => onOpen(item)}
        className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-md px-2 py-2.5 text-left hover:bg-semantic-hover-primary"
      >
        {content}
      </button>
    </li>
  );
}

/** The one-line all-clear above a list with no open issue. */
function AllClear({ lastOutage }: { lastOutage: FailoverItem | null }) {
  return (
    <div className="mb-2 flex items-start gap-2 rounded-md bg-semantic-bg-success-secondary px-3 py-2">
      <SuccessIcon className="mt-0.5 h-4 w-4 shrink-0 text-semantic-fg-success" aria-hidden />
      <div className="text-xs">
        <p className="font-semibold text-semantic-text-primary">Nothing needs attention</p>
        {lastOutage && (
          <p className="text-semantic-text-secondary">
            Last outage {lastOutage.date}
            {lastOutage.site && ` · ${lastOutage.site}`} · lasted {formatDuration(lastOutage.duration)}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Every service of the selected kind, worst first: open outages and services
 * down, then degraded ones, then outages resolved this week, then the rest.
 * It follows the chips, so selecting "Cloud" lists the cloud services.
 */
export function AttentionPanel({
  title,
  className = "",
  siteFilter,
  onClearSiteFilter,
  items,
  lastOutage,
  isLoading,
  onOpen,
  onViewHistory,
}: {
  /** What the list holds, e.g. "All services" or "Cloud services". */
  title: string;
  className?: string;
  siteFilter: SiteFilter | null;
  onClearSiteFilter: () => void;
  items: AttentionItem[];
  /** Shown when nothing is open, so the all-clear says when trouble last happened. */
  lastOutage: FailoverItem | null;
  isLoading: boolean;
  onOpen: (item: AttentionItem) => void;
  onViewHistory: () => void;
}) {
  const openCount = items.filter(isOpenIssue).length;
  const [status, setStatus] = useState<StatusFilter>("all");
  const [query, setQuery] = useState("");
  const siteIds = siteFilter?.siteIds;
  // Counts follow the search and the site, so a pill never promises rows it hides.
  const searched = useMemo(() => filterServiceList(items, { query, siteIds }), [items, query, siteIds]);
  const counts = countByStatus(searched);
  const shown = useMemo(() => filterServiceList(searched, { status }), [searched, status]);
  const isFiltered = status !== "all" || query.trim() !== "" || Boolean(siteFilter);

  return (
    <OverviewPanel
      className={className}
      title={title}
      subtitle={openCount > 0 ? `${openCount} need attention · issues first` : "Issues first"}
      action={
        openCount > 0 ? (
          <span className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-semantic-bg-danger-secondary px-2 text-xs font-bold text-semantic-text-danger">
            {openCount}
          </span>
        ) : undefined
      }
      footer={
        <div className="flex justify-end px-5 py-3">
          <PanelLink label="Outage history" href="/kontrol/outages" onClick={onViewHistory} />
        </div>
      }
    >
      <div className="mb-3 space-y-2">
        <label className="flex h-8 items-center gap-2 rounded-md border border-semantic-border-primary bg-semantic-bg-primary px-2.5 focus-within:border-semantic-border-brand-primary">
          <SearchIcon className="h-3.5 w-3.5 shrink-0 text-semantic-text-secondary" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search name, tag, site or type"
            aria-label="Search services"
            className="w-full bg-transparent text-xs text-semantic-text-primary outline-none placeholder:text-semantic-text-secondary"
          />
        </label>
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by status">
          {STATUS_PILLS.map((pill) => {
            const isSelected = status === pill.value;
            return (
              <button
                key={pill.value}
                type="button"
                aria-pressed={isSelected}
                onClick={() => setStatus(pill.value)}
                disabled={!isSelected && counts[pill.value] === 0}
                className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors disabled:opacity-40 ${
                  isSelected
                    ? "border-semantic-border-brand-primary bg-semantic-bg-brand-secondary text-semantic-text-brand"
                    : "border-semantic-border-primary text-semantic-text-secondary hover:bg-semantic-hover-primary"
                }`}
              >
                {pill.label} {counts[pill.value]}
              </button>
            );
          })}
          {siteFilter && (
            <button
              type="button"
              onClick={onClearSiteFilter}
              className="inline-flex items-center gap-1 rounded-full border border-semantic-border-brand-primary bg-semantic-bg-brand-secondary px-2.5 py-0.5 text-[11px] font-semibold text-semantic-text-brand"
              aria-label={`Show every site, not only ${siteFilter.label}`}
            >
              {siteFilter.label}
              <CloseIcon className="h-3 w-3" aria-hidden />
            </button>
          )}
        </div>
      </div>
      <AttentionBody
        // A new kind, status or site refills the whole list; typing in the
        // search only brings in the rows it adds.
        key={`${title}|${status}|${siteIds?.join(",") ?? ""}`}
        items={shown}
        isFiltered={isFiltered}
        hasOpenIssue={openCount > 0}
        lastOutage={lastOutage}
        isLoading={isLoading}
        onOpen={onOpen}
      />
    </OverviewPanel>
  );
}

function AttentionBody({
  items,
  isFiltered,
  hasOpenIssue,
  lastOutage,
  isLoading,
  onOpen,
}: {
  items: AttentionItem[];
  isFiltered: boolean;
  hasOpenIssue: boolean;
  lastOutage: FailoverItem | null;
  isLoading: boolean;
  onOpen: (item: AttentionItem) => void;
}) {
  if (isLoading) {
    return (
      <div className="space-y-4 py-2" aria-busy="true">
        {[0, 1, 2, 3].map((index) => (
          <SkeletonLine key={index} h={32} decorative />
        ))}
      </div>
    );
  }
  if (items.length === 0 && isFiltered) {
    return <p className="py-6 text-center text-sm text-semantic-text-secondary">No service matches these filters.</p>;
  }
  if (items.length === 0) {
    return (
      <>
        <AllClear lastOutage={lastOutage} />
        <p className="py-6 text-center text-sm text-semantic-text-secondary">No services of this type.</p>
      </>
    );
  }
  return (
    <>
      {!hasOpenIssue && !isFiltered && <AllClear lastOutage={lastOutage} />}
      {/* The list scrolls inside the panel: the panel is as tall as the map
          beside it, however many services the tenant has. */}
      <ul className="-mr-2 min-h-0 flex-1 divide-y divide-semantic-border-primary overflow-y-auto pr-2">
        {items.map((item, index) => (
          <AttentionRow key={item.key} item={item} index={index} onOpen={onOpen} />
        ))}
      </ul>
    </>
  );
}
